import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ServerDomainInfo } from '@/api/type-server'
import {
  Button,
  ButtonRadio,
  ButtonRadioItem,
  Divider,
  Loading,
  Pagination,
  Trans,
} from '@/components'
import { Dropdown } from '@/components/base/Dropdown'
import { CollapseTableRow } from '@/components/CollapseTableRow'
import { EmptyView } from '@/components/EmptyView'
import { Network } from '@/models/network.ts'
import { ClusterCreateOrUpdateModal } from '@/pages/mesh-clustering/components/ClusterCreateOrUpdateModal'
import ClusterDetail from '@/pages/mesh-clustering/components/ClusterDetail'

import { ClusterDeleteModal } from './components/ClusterDeleteModal'
import { EditableNetworkName } from './components/EditableClusterName'

const REGION_GLOBAL = -1

export function MeshClusteringListPage() {
  const { t } = useTranslation()
  const statusFilters: ButtonRadioItem<string>[] = [
    {
      label: <Trans i18nKey="mesh.type.networkGroup">Network Group</Trans>,
      value: 'network-group',
    },
    {
      label: <Trans i18nKey="mesh.type.loadBalancer">Load Balancer</Trans>,
      value: 'load-balancer',
      disabled: true,
    },
    {
      label: <Trans i18nKey="mesh.type.cluster">Cluster</Trans>,
      value: 'cluster',
      disabled: true,
    },
  ]
  const [filter, setFilter] = useState<ButtonRadioItem<string> | undefined>(statusFilters[0])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const pageSize = 10
  const [networks, setNetworks] = useState<Network[]>([])
  const [createMesh, setCreateMesh] = useState<boolean>()
  const [modifyNetwork, setModifyNetwork] = useState<Network>()
  const [deleteNetwork, setDeleteNetwork] = useState<Network>()
  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const [regionFilterCriteria, setRegionFilterCriteria] = useState<number>(REGION_GLOBAL)

  const getDomains = useCallback(async () => {
    try {
      const res = await API.getDomainList()
      if (res.code === 0) {
        setDomainInfos(res.domainInfos)
      }
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    getDomains()
  }, [])

  const getClusterList = useCallback(
    async (p = page, force: boolean) => {
      if (force) {
        setLoading(true)
      }
      try {
        const res = await API.getMeshList({
          page: p,
          per_page: pageSize,
          regions:
            regionFilterCriteria === REGION_GLOBAL
              ? domainInfos.map((info) => info.domain)
              : [regionFilterCriteria],
          region_filter_criteria: 0,
        })
        if (res.code === 0) {
          setPage(res.pagination.page)
          setTotal(res.pagination.total)
          setNetworks(res.networks?.map((data) => new Network(data)))
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [page, filter, regionFilterCriteria, domainInfos],
  )

  useEffect(() => {
    getClusterList(1, true)
  }, [page, filter, regionFilterCriteria, domainInfos])

  const onNetworkUpdate = (updatedNetwork: Network) => {
    const updatedNetworks = networks?.map((network) =>
      network.resID === updatedNetwork.resID ? updatedNetwork : network,
    )

    setNetworks(updatedNetworks)
  }

  return (
    <div className="card bg-white h-full overflow-y-auto">
      <div className="flex items-start justify-between mb-4">
        <div className="h6">
          <Trans i18nKey="mesh.title">Mesh Clustering</Trans>
        </div>
        <Button
          type="secondary"
          onClick={() => {
            setCreateMesh(true)
          }}
        >
          <Trans i18nKey="mesh.createBtn">Create New</Trans>
        </Button>
      </div>
      <Divider />
      <div className="flex justify-start items-center gap-8">
        <ButtonRadio
          value={filter?.value}
          items={statusFilters}
          onChange={(e) => {
            setFilter(statusFilters.find((i) => i.value === e))
          }}
        />
        <Dropdown
          rootClassName="t-sb w-60"
          className="h-11"
          options={[
            { label: t('common.region.groupGlobal'), value: REGION_GLOBAL, className: 't-sm' },
            ...domainInfos.map((info, index) => ({
              label: t(`common.region.domain${info.domain}`),
              value: info.domain,
              className: 't-sm',
              separatorBefore: index === 0,
            })),
          ]}
          value={regionFilterCriteria}
          onChange={(value) => {
            setRegionFilterCriteria(Number(value))
          }}
        />
      </div>
      {loading ? (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      ) : networks.length > 0 ? (
        <table className="table-card mt-4 table-fixed">
          <thead>
            <tr>
              <th colSpan={4}>
                <Trans i18nKey="mesh.tableHeaderID">ID</Trans>
              </th>
              <th colSpan={9}>
                <Trans i18nKey="mesh.tableHeaderName">Name</Trans>
              </th>
              <th colSpan={3}>
                <Trans i18nKey="mesh.tableHeaderRegion">Region</Trans>
              </th>
              <th colSpan={3}>
                <Trans i18nKey="mesh.tableHeaderCapacity">Capacity</Trans>
              </th>
              <th colSpan={5}>
                <Trans i18nKey="mesh.tableHeaderAction">Action</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {networks.map((network) => (
              <CollapseTableRow
                key={network.resID}
                colSpan={24}
                tableClassName="group"
                content={
                  <div className="px-4 py-2 mx-4 bg-neutral-50 rounded-xl group">
                    <ClusterDetail
                      network={network}
                      regionDisplayName={
                        domainInfos.find((domainInfo) => {
                          return domainInfo.domain === network.domain
                        })?.displayName || '-'
                      }
                      onNetworkUpdate={onNetworkUpdate}
                    />
                  </div>
                }
              >
                <td colSpan={4} className="t-sm">
                  {network.resID}
                </td>
                <td colSpan={9} className="t-sm">
                  <div className="flex items-center">
                    <EditableNetworkName
                      network={network}
                      onUpdate={() => getClusterList(page, true)}
                    />
                  </div>
                </td>
                <td colSpan={3}>
                  {domainInfos.find((domainInfo) => {
                    return domainInfo.domain === network.domain
                  })?.displayName || '-'}
                </td>
                <td colSpan={3}>
                  {network?.capacity?.used ?? 0}/
                  <span className="text-tertiary">{network?.capacity?.total ?? 32}</span>
                </td>
                <td colSpan={4}>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-4">
                      {network?.capacity?.used === 0 && (
                        <div
                          className="text-red-500 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation()
                            setDeleteNetwork(network)
                          }}
                        >
                          <Trans i18nKey="mesh.net.deleteBtn">Delete</Trans>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </CollapseTableRow>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="mt-4 mb-2 ">
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg">No data</Trans>
              </span>
            }
          />
        </div>
      )}

      {total > 0 && !loading && (
        <Pagination
          size={pageSize}
          total={total}
          current={page}
          className="justify-end mt-4"
          onChange={(page) => getClusterList(page, true)}
        />
      )}

      {createMesh ? (
        <ClusterCreateOrUpdateModal
          onSuccess={() => {
            setCreateMesh(undefined)
            setModifyNetwork(undefined)
            getClusterList(page, true)
          }}
          onClose={() => {
            setCreateMesh(undefined)
            setModifyNetwork(undefined)
          }}
          network={modifyNetwork}
          domainInfos={domainInfos}
        />
      ) : null}
      {deleteNetwork ? (
        <ClusterDeleteModal
          network={deleteNetwork}
          onSuccess={() => {
            setDeleteNetwork(undefined)
            getClusterList(page, true)
          }}
          onClose={() => setDeleteNetwork(undefined)}
        />
      ) : null}
    </div>
  )
}
